import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { adminAPI } from '@/services/api';
import { User, Customer } from '@/types';

interface ApproveUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onApprove: (userId: number, customerId?: number, companyName?: string) => Promise<void>;
  onCreateCustomer?: (user: User) => void;
  showCreateCustomer?: boolean;
  onSuccess?: () => void;
}

export const ApproveUserDialog: React.FC<ApproveUserDialogProps> = ({
  open,
  onOpenChange,
  user,
  onApprove,
  onCreateCustomer,
  showCreateCustomer = true,
  onSuccess,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [approveMode, setApproveMode] = useState<'select' | 'create'>('select');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && user) {
      // 다이얼로그가 열릴 때 고객사 목록 불러오기
      const fetchCustomers = async () => {
        try {
          const response = await adminAPI.getCustomers(0, 1000);
          setCustomers(response.customers || []);
        } catch (err: any) {
          setError(err.response?.data?.detail || '고객사 목록을 불러오는데 실패했습니다.');
        }
      };
      fetchCustomers();
      
      // 초기화
      setSelectedCustomerId(null);
      setNewCompanyName('');
      setApproveMode('select');
      setError('');
    }
  }, [open, user]);

  const handleApprove = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      if (approveMode === 'create' && onCreateCustomer) {
        // 고객사 생성 모드
        await onCreateCustomer(user);
      } else if (approveMode === 'select' && selectedCustomerId) {
        // 기존 고객사 선택 모드
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        await onApprove(user.id, selectedCustomerId, selectedCustomer?.customer_name);
      } else {
        setError('고객사를 선택하거나 생성해주세요.');
        setLoading(false);
        return;
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || '승인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>사용자 승인</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>사용자 이메일</Label>
            <Input value={user.email} disabled />
          </div>

          {showCreateCustomer && onCreateCustomer && (
            <div>
              <Label>승인 방식</Label>
              <Select
                value={approveMode}
                onValueChange={(value: 'select' | 'create') => setApproveMode(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">기존 고객사 선택</SelectItem>
                  <SelectItem value="create">새 고객사 생성</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {approveMode === 'select' ? (
            <div>
              <Label>고객사 선택</Label>
              <Select
                value={selectedCustomerId?.toString() || ''}
                onValueChange={(value) => setSelectedCustomerId(parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="고객사를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id.toString()}>
                      {customer.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>새 고객사 이름</Label>
              <Input
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="고객사 이름을 입력하세요"
              />
            </div>
          )}

          {error && (
            <AlertMessage
              type="error"
              message={error}
              onClose={() => setError('')}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            취소
          </Button>
          <Button 
            onClick={handleApprove} 
            disabled={loading || (approveMode === 'select' && !selectedCustomerId) || (approveMode === 'create' && !newCompanyName)}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            {loading ? '처리 중...' : '승인'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

